import { PublicClientApplication } from "@azure/msal-browser";
import { renderTopbarNavigation } from "../public/assets/topbar_navigation.js";

const portalUrl = value => window.PORTAL_BASE?.url(value) || value;
const config = window.PORTAL_CONFIG;
if (!config) throw new Error("PORTAL_CONFIG fehlt. config.js wurde nicht geladen.");

// A private login page must never fall back to the company's B2C configuration.
if (document.body?.dataset.authEntry === 'oidc' && config.authMode !== 'oidc') {
  throw new Error('Private SSO requires authMode=oidc in the portal configuration.');
}
const privateOidc = config.authMode === 'oidc';
const b2c = config.b2c || {};
const authorityHost = privateOidc ? '' : `${b2c.tenantName}.b2clogin.com`;
const authority = privateOidc ? '' : `https://${authorityHost}/${b2c.tenantDomain}/${b2c.policy}`;
const authRedirectUri = (() => {
  const path = window.location.pathname || "/";
  const slash = path.lastIndexOf("/");
  const leaf = path.slice(slash + 1);
  const basePath = path.endsWith("/")
    ? path
    : leaf.includes(".")
      ? path.slice(0, slash + 1)
      : `${path}/`;
  return `${window.location.origin}${basePath}redirect.html`;
})();

const msal = privateOidc ? null : new PublicClientApplication({
  auth: {
    clientId: b2c.clientId,
    authority,
    knownAuthorities: [authorityHost],
    redirectUri: authRedirectUri,
    postLogoutRedirectUri: authRedirectUri
  },
  cache: { cacheLocation: "sessionStorage" },
  system: { popupBridgeTimeout: Number(config.b2c.popupTimeoutMs ?? 300000) }
});

const loginRequest = {
  scopes: ["openid", "profile", "email"],
  redirectUri: authRedirectUri
};

const el = {
  portalTitle: document.querySelector("#portal-title"),
  loginCard: document.querySelector("#login-card"),
  loginButton: document.querySelector("#login-button"),
  logoutButton: document.querySelector("#logout-button"),
  loginMessage: document.querySelector("#login-message"),
  portal: document.querySelector("#portal"),
  portalTabs: document.querySelector("#portal-tabs"),
  portalMainNavigation: document.querySelector("#portal-main-navigation"),
  portalAdministrationButton: document.querySelector("#portal-administration-button"),
  portalAdministrationItems: document.querySelector("#portal-administration-items"),
  userBadge: document.querySelector("#user-badge"),
  search: document.querySelector("#search"),
  searchField: document.querySelector("#search-field"),
  resultCount: document.querySelector("#result-count"),
  searchEmpty: document.querySelector("#search-empty"),
  searchResults: document.querySelector("#search-results"),
  searchPagination: document.querySelector("#search-pagination"),
  errorBox: document.querySelector("#error-box")
};

let currentCapabilities = [];
let searchColumns = [];
let searchRows = [];
let expandedProductIds = new Set();
let searchRequestId = 0;
let searchTimer = null;
let currentPage = 1;
let pageCount = 0;
let searchTotal = 0;
const SEARCH_PAGE_SIZE_KEY = "portal.productsPerPage";
const SEARCH_PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function initialSearchLimit() {
  try {
    const stored = Number(window.localStorage.getItem(SEARCH_PAGE_SIZE_KEY));
    if (SEARCH_PAGE_SIZE_OPTIONS.includes(stored)) return stored;
  } catch {}
  return 25;
}

let searchLimit = initialSearchLimit();

const ICONS = {
  download: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>`
};

function formatBytes(bytes) {
  const units = ["B", "KB", "MB", "GB"];
  let value = Number(bytes || 0);
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value.toFixed(unit === 0 ? 0 : 1)} ${units[unit]}`;
}

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  }).format(date);
}

function showError(message) {
  el.errorBox.textContent = message;
  el.errorBox.classList.remove("hidden");
}

function clearError() {
  el.errorBox.textContent = "";
  el.errorBox.classList.add("hidden");
}

function renderPortalTabs(capabilities = []) {
  currentCapabilities = Array.isArray(capabilities) ? capabilities : [];
  const has = key => currentCapabilities.includes("*") || currentCapabilities.includes(key);

  const items = [{ id: "downloads", label: "Downloads", group: "main", href: portalUrl("/") }];
  if (has("view_files")) items.push({ id: "data", label: "Dateien", group: "main", href: portalUrl("/admin/#data") });
  if (has("view_product_data")) items.push({ id: "products", label: "Produkte", group: "main", href: portalUrl("/admin/#products") });
  if (has("view_file_classification") || has("view_file_permissions")) items.push({ id: "classification", label: "Klassifikation", group: "main", href: portalUrl("/admin/#classification") });
  if (has("view_faq")) items.push({ id: "faq", label: "Startseite", group: "administration", href: portalUrl("/admin/#faq") });
  if (has("view_gui_languages")) items.push({ id: "languages", label: "Sprachen", group: "administration", href: portalUrl("/admin/#languages") });
  if (has("view_role_bindings")) items.push({ id: "users", label: "Benutzer", group: "administration", href: portalUrl("/admin/#users") });
  if (has("view_roles") || has("view_role_assignment_permissions")) items.push({ id: "roles", label: "Rollen", group: "administration", href: portalUrl("/admin/#roles") });
  if (has("view_system_settings") || has("view_session_security") || has("view_file_security")) items.push({ id: "system", label: "Systemeinstellungen", group: "administration", href: portalUrl("/admin/#system") });
  if (has("view_database_backups")) items.push({ id: "backup", label: "Sicherung & Recovery", group: "administration", href: portalUrl("/admin/#backup") });
  if (has("view_audit")) items.push({ id: "audit", label: "Audit", group: "administration", href: portalUrl("/admin/#audit") });

  renderTopbarNavigation({
    mainContainer: el.portalMainNavigation,
    administrationButton: el.portalAdministrationButton,
    administrationItems: el.portalAdministrationItems,
    items,
    activeId: "downloads"
  });
  el.portalTabs.classList.toggle("hidden", items.length === 0);
}

function resetSearchState(message = "Suchbegriff eingeben, um Produktdaten zu durchsuchen.") {
  searchRows = [];
  expandedProductIds = new Set();
  el.searchResults.replaceChildren();
  el.searchResults.classList.add("hidden");
  el.searchPagination.replaceChildren();
  el.searchPagination.classList.add("hidden");
  el.searchEmpty.classList.remove("hidden");
  const text = el.searchEmpty.querySelector("p");
  if (text) text.textContent = message;
  el.resultCount.textContent = "";
}

function showLoggedOut(message = "") {
  el.loginCard.classList.remove("hidden");
  el.portal.classList.add("hidden");
  el.logoutButton.classList.add("hidden");
  el.userBadge.classList.add("hidden");
  el.loginMessage.textContent = message;
  currentCapabilities = [];
  searchColumns = [];
  renderTopbarNavigation({
    mainContainer: el.portalMainNavigation,
    administrationButton: el.portalAdministrationButton,
    administrationItems: el.portalAdministrationItems,
    items: []
  });
  el.portalTabs.classList.add("hidden");
  populateSearchFields([]);
  resetSearchState();
}

function showLoggedIn(email, roleLabel = "", capabilities = []) {
  el.loginCard.classList.add("hidden");
  el.portal.classList.remove("hidden");
  el.logoutButton.classList.remove("hidden");
  el.userBadge.classList.remove("hidden");
  el.userBadge.textContent = roleLabel ? `${email} · ${roleLabel}` : email;
  el.loginMessage.textContent = "";
  renderPortalTabs(capabilities);
}

async function api(path, options = {}) {
  const response = await fetch(`${config.apiBase}${path}`, {
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(options.headers || {})
    },
    ...options
  });

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json") ? await response.json() : null;
  if (!response.ok) throw new Error(data?.error || `HTTP ${response.status}`);
  return data;
}

async function createServerSession(idToken) {
  return api("/session.php", {
    method: "POST",
    body: JSON.stringify({ idToken })
  });
}

function directDownloadHref(file) {
  const base = String(config.apiBase || "/api").replace(/\/$/, "");
  return portalUrl(`${base}/indexed_download.php?id=${encodeURIComponent(file.indexedFileId)}`);
}

function requestedReturnTo() {
  const params = new URLSearchParams(window.location.search);
  const value = params.get("returnTo");
  if (!value || !value.startsWith("/") || value.startsWith("//")) return null;
  return value;
}

function continueRequestedDownload() {
  const returnTo = requestedReturnTo();
  if (returnTo) window.location.assign(returnTo);
}

function populateSearchFields(columns = []) {
  searchColumns = Array.isArray(columns) ? columns : [];
  const previous = el.searchField.value;
  const options = [new Option("Alle Felder", "")];

  for (const column of searchColumns) {
    const position = Number(column.position);
    if (!Number.isInteger(position) || position < 0) continue;
    const option = document.createElement("option");
    option.value = String(column.position);
    option.textContent = String(column.displayName || column.name || `Spalte ${position + 1}`);
    options.push(option);
  }

  el.searchField.replaceChildren(...options);
  if ([...el.searchField.options].some(option => option.value === previous)) {
    el.searchField.value = previous;
  }
}

function valueForColumn(row, position) {
  const field = (row.fields || []).find(item => Number(item.position) === Number(position));
  return field ? String(field.value ?? "") : "";
}

function makeCell(text, className = "") {
  const td = document.createElement("td");
  if (className) td.className = className;
  td.textContent = text;
  return td;
}

function makeFileTypeCell(file) {
  const td = document.createElement("td");
  td.className = "file-type-cell";
  const label = document.createElement("div");
  label.className = "file-type-label";
  label.textContent = String(file.fileTypeLabel || "Datei");
  td.append(label);
  const description = String(file.fileTypeDescription || "").trim();
  if (description) {
    const explanation = document.createElement("small");
    explanation.className = "file-type-description muted";
    explanation.textContent = description;
    td.append(explanation);
  }
  return td;
}

function buildFilesPanel(row) {
  const wrap = document.createElement("div");
  wrap.className = "result-files";

  const head = document.createElement("div");
  head.className = "result-files-head";
  const title = document.createElement("strong");
  title.textContent = "Freigegebene Dateien";
  const count = document.createElement("span");
  count.className = "badge badge-muted";
  count.textContent = `${(row.files || []).length} Datei${(row.files || []).length === 1 ? "" : "en"}`;
  head.append(title, count);
  wrap.append(head);

  if (!Array.isArray(row.files) || row.files.length === 0) {
    const empty = document.createElement("div");
    empty.className = "empty";
    empty.textContent = "Für dieses Produkt sind für Ihre Rollen keine Dateien sichtbar.";
    wrap.append(empty);
    return wrap;
  }

  const tableWrap = document.createElement("div");
  tableWrap.className = "table-wrap";
  const table = document.createElement("table");
  table.className = "table result-file-table";

  const thead = document.createElement("thead");
  const header = document.createElement("tr");
  ["Dateityp", "Datei", "Stand", "Größe", "Aktion"].forEach(label => {
    const th = document.createElement("th");
    th.scope = "col";
    th.textContent = label;
    header.append(th);
  });
  thead.append(header);

  const tbody = document.createElement("tbody");
  for (const file of row.files) {
    const tr = document.createElement("tr");
    tr.dataset.indexedFileId = String(file.indexedFileId || "");
    tr.dataset.previewKind = String(file.previewKind || "");
    tr.append(
      makeFileTypeCell(file),
      makeCell(String(file.name || ""), "file-name-cell"),
      makeCell(formatDate(file.modified)),
      makeCell(formatBytes(file.size))
    );

    const action = document.createElement("td");
    if (file.canDownload) {
      const link = document.createElement("a");
      link.className = "btn btn-primary btn-sm";
      link.href = directDownloadHref(file);
      link.innerHTML = `${ICONS.download}<span>Download</span>`;
      action.append(link);
    } else {
      const badge = document.createElement("span");
      badge.className = "badge badge-muted";
      badge.textContent = "Nur sichtbar";
      action.append(badge);
    }
    tr.append(action);
    tbody.append(tr);
  }

  table.append(thead, tbody);
  tableWrap.append(table);
  wrap.append(tableWrap);
  return wrap;
}

function renderSearchResults() {
  el.searchResults.replaceChildren();

  if (searchRows.length === 0) {
    el.searchResults.classList.add("hidden");
    el.searchEmpty.classList.remove("hidden");
    const text = el.searchEmpty.querySelector("p");
    if (text) text.textContent = el.search.value.trim() ? "Keine Treffer für diese Suche." : "Keine Produktdaten vorhanden.";
    el.resultCount.textContent = "0 Treffer";
    return;
  }

  el.searchEmpty.classList.add("hidden");
  el.searchResults.classList.remove("hidden");
  el.resultCount.textContent = searchTotal > searchLimit
    ? `${searchRows.length} von ${searchTotal}`
    : `${searchTotal} Treffer`;

  const table = document.createElement("table");
  table.className = "table result-table";

  const thead = document.createElement("thead");
  const header = document.createElement("tr");

  const expandHead = document.createElement("th");
  expandHead.className = "result-expand-col";
  expandHead.scope = "col";
  const hidden = document.createElement("span");
  hidden.className = "visually-hidden";
  hidden.textContent = "Dateien";
  expandHead.append(hidden);
  header.append(expandHead);

  searchColumns.map(column => {
    const th = document.createElement("th");
    th.scope = "col";
    th.textContent = String(column.displayName || column.name || `Spalte ${Number(column.position) + 1}`);
    header.append(th);
    return th;
  });

  thead.append(header);
  const tbody = document.createElement("tbody");

  for (const row of searchRows) {
    const id = Number(row.id);
    const open = expandedProductIds.has(id);

    const tr = document.createElement("tr");
    tr.className = "product-result-row";

    const expandCell = document.createElement("td");
    expandCell.className = "result-expand-col";
    const toggle = document.createElement("button");
    toggle.type = "button";
    toggle.className = "btn btn-icon btn-toggle";
    toggle.dataset.productId = String(id);
    toggle.setAttribute("aria-expanded", open ? "true" : "false");
    toggle.setAttribute("aria-label", open ? "Dateien ausblenden" : "Dateien anzeigen");
    toggle.textContent = open ? "−" : "+";
    expandCell.append(toggle);
    tr.append(expandCell);

    for (const column of searchColumns) {
      const td = makeCell(valueForColumn(row, column.position), "product-field-cell");
      td.dataset.columnPosition = String(column.position);
      tr.append(td);
    }

    const detail = document.createElement("tr");
    detail.className = "product-detail-row";
    detail.hidden = !open;
    const detailCell = document.createElement("td");
    detailCell.colSpan = searchColumns.length + 1;
    detailCell.append(buildFilesPanel(row));
    detail.append(detailCell);

    toggle.addEventListener("click", () => {
      if (expandedProductIds.has(id)) expandedProductIds.delete(id);
      else expandedProductIds.add(id);
      renderSearchResults();
    });

    tbody.append(tr, detail);
  }

  table.append(thead, tbody);
  el.searchResults.append(table);
  renderPagination();
}

function renderPagination() {
  el.searchPagination.replaceChildren();

  if (searchTotal <= 0) {
    el.searchPagination.classList.add("hidden");
    return;
  }

  el.searchPagination.classList.remove("hidden");

  const navigation = document.createElement("div");
  navigation.className = "search-pagination-nav";

  const first = document.createElement("button");
  first.type = "button";
  first.className = "btn btn-secondary btn-sm search-page-edge";
  first.textContent = "<|";
  first.title = "Erste Seite";
  first.setAttribute("aria-label", "Erste Seite");
  first.disabled = currentPage <= 1;
  first.addEventListener("click", () => runSearch(1));

  const previous = document.createElement("button");
  previous.type = "button";
  previous.className = "btn btn-secondary btn-sm";
  previous.textContent = "Zurück";
  previous.disabled = currentPage <= 1;
  previous.addEventListener("click", () => runSearch(currentPage - 1));

  const status = document.createElement("span");
  status.className = "search-page-status";
  status.textContent = `Seite ${currentPage} von ${Math.max(1, pageCount)} · ${searchTotal} Produkte`;

  const next = document.createElement("button");
  next.type = "button";
  next.className = "btn btn-secondary btn-sm";
  next.textContent = "Weiter";
  next.disabled = currentPage >= pageCount;
  next.addEventListener("click", () => runSearch(currentPage + 1));

  const last = document.createElement("button");
  last.type = "button";
  last.className = "btn btn-secondary btn-sm search-page-edge";
  last.textContent = "|>";
  last.title = "Letzte Seite";
  last.setAttribute("aria-label", "Letzte Seite");
  last.disabled = currentPage >= pageCount;
  last.addEventListener("click", () => runSearch(Math.max(1, pageCount)));

  navigation.append(first, previous, status, next, last);

  const settings = document.createElement("div");
  settings.className = "search-pagination-settings";

  const gotoLabel = document.createElement("label");
  gotoLabel.className = "search-pagination-field";
  const gotoText = document.createElement("span");
  gotoText.textContent = "Gehe zu Seite";
  const gotoInput = document.createElement("input");
  gotoInput.type = "number";
  gotoInput.className = "search-pagination-input";
  gotoInput.min = "1";
  gotoInput.max = String(Math.max(1, pageCount));
  gotoInput.step = "1";
  gotoInput.value = String(currentPage);
  gotoInput.disabled = pageCount <= 1;
  gotoInput.setAttribute("aria-label", "Gehe zu Seite");
  const goToRequestedPage = () => {
    const requested = Math.max(1, Math.min(Math.max(1, pageCount), Number(gotoInput.value) || currentPage));
    gotoInput.value = String(requested);
    if (requested !== currentPage) runSearch(requested);
  };
  gotoInput.addEventListener("change", goToRequestedPage);
  gotoInput.addEventListener("keydown", event => {
    if (event.key === "Enter") {
      event.preventDefault();
      goToRequestedPage();
    }
  });
  gotoLabel.append(gotoText, gotoInput);

  const pageSizeLabel = document.createElement("label");
  pageSizeLabel.className = "search-pagination-field";
  const pageSizeText = document.createElement("span");
  pageSizeText.textContent = "Produkte pro Seite";
  const pageSizeSelect = document.createElement("select");
  pageSizeSelect.className = "search-pagination-select";
  pageSizeSelect.setAttribute("aria-label", "Produkte pro Seite");
  for (const size of SEARCH_PAGE_SIZE_OPTIONS) {
    const option = document.createElement("option");
    option.value = String(size);
    option.textContent = String(size);
    option.selected = size === searchLimit;
    pageSizeSelect.append(option);
  }
  pageSizeSelect.addEventListener("change", () => {
    const previousLimit = searchLimit;
    const selected = Number(pageSizeSelect.value);
    if (!SEARCH_PAGE_SIZE_OPTIONS.includes(selected) || selected === previousLimit) return;
    const firstVisibleIndex = Math.max(0, (currentPage - 1) * previousLimit);
    searchLimit = selected;
    try { window.localStorage.setItem(SEARCH_PAGE_SIZE_KEY, String(selected)); } catch {}
    const targetPage = Math.floor(firstVisibleIndex / selected) + 1;
    runSearch(targetPage);
  });
  pageSizeLabel.append(pageSizeText, pageSizeSelect);

  settings.append(gotoLabel, pageSizeLabel);
  el.searchPagination.append(navigation, settings);
}

async function loadSearchSchema() {
  clearError();
  await runSearch(1);
}

async function runSearch(page = 1) {
  const query = el.search.value.trim();
  const requestId = ++searchRequestId;
  const requestedPage = Math.max(1, Number(page) || 1);

  const params = new URLSearchParams({ q: query, limit: String(searchLimit), page: String(requestedPage) });
  if (query !== "" && el.searchField.value !== "") params.set("column", el.searchField.value);

  try {
    const data = await api(`/product_search.php?${params.toString()}`);
    if (requestId !== searchRequestId) return;

    const selectedField = el.searchField.value;
    populateSearchFields(data.columns || []);
    if ([...el.searchField.options].some(option => option.value === selectedField)) {
      el.searchField.value = selectedField;
    }

    searchRows = Array.isArray(data.rows) ? data.rows : [];
    currentPage = Number(data.page || requestedPage);
    pageCount = Number(data.pageCount || 0);
    searchTotal = Number(data.total || 0);
    searchLimit = Number(data.limit || searchLimit);
    expandedProductIds = new Set();
    clearError();
    renderSearchResults();
  } catch (error) {
    if (requestId !== searchRequestId) return;
    searchRows = [];
    currentPage = 1;
    pageCount = 0;
    searchTotal = 0;
    expandedProductIds = new Set();
    showError(error.message || "Produktsuche konnte nicht ausgeführt werden.");
    resetSearchState("Die Suche konnte nicht ausgeführt werden.");
  }
}

function scheduleSearch(delay = 180) {
  if (searchTimer !== null) window.clearTimeout(searchTimer);

  searchTimer = window.setTimeout(() => {
    searchTimer = null;
    runSearch(1);
  }, delay);
}

function startPrivateOidc(provider) {
  if (!privateOidc || !provider || !/^[a-z][a-z0-9_-]{0,31}$/.test(provider)) {
    el.loginMessage.textContent = 'OIDC-Anbieter nicht konfiguriert.';
    return;
  }
  const returnTo = window.location.pathname + window.location.search;
  const base = String(config.apiBase || '/api').replace(/\/$/, '');
  window.location.assign(portalUrl(`${base}/oidc_start.php?provider=${encodeURIComponent(provider)}&returnTo=${encodeURIComponent(returnTo)}`));
}

async function configureOidcLogin() {
  if (!privateOidc) return;
  el.loginButton.disabled = true;
  el.loginButton.textContent = 'OIDC-Anbieter werden geladen …';
  try {
    const result = await api('/oidc_providers.php');
    const providers = Array.isArray(result.providers) ? result.providers : [];
    if (!providers.length) throw new Error('Kein privater OIDC-Anbieter konfiguriert.');
    const primary = providers[0];
    el.loginButton.dataset.oidcProvider = primary.id;
    el.loginButton.textContent = primary.label;
    el.loginButton.disabled = false;
    for (const provider of providers.slice(1)) {
      if (!/^[a-z][a-z0-9_-]{0,31}$/.test(provider.id)) continue;
      const option = document.createElement('button');
      option.type = 'button';
      option.className = el.loginButton.className;
      option.textContent = provider.label;
      option.addEventListener('click', () => startPrivateOidc(provider.id));
      el.loginButton.insertAdjacentElement('afterend', option);
    }
  } catch (error) {
    el.loginButton.textContent = 'Privater SSO-Login nicht verfügbar';
    el.loginMessage.textContent = error.message || 'OIDC-Anbieter konnten nicht geladen werden.';
  }
}

async function login() {
  el.loginMessage.textContent = '';
  if (privateOidc) {
    startPrivateOidc(el.loginButton.dataset.oidcProvider);
    return;
  }
  try {
    const result = await msal.loginPopup(loginRequest);
    const session = await createServerSession(result.idToken);
    showLoggedIn(session.email, session.roleLabel, session.capabilities);

    if (requestedReturnTo()) {
      continueRequestedDownload();
      return;
    }

    await loadSearchSchema();
  } catch (error) {
    console.error(error);
    showLoggedOut(error.message || "Anmeldung fehlgeschlagen.");
  }
}

async function restoreSession() {
  try {
    const status = await api("/status.php");
    if (status.authenticated) {
      showLoggedIn(status.email, status.roleLabel, status.capabilities);

      if (requestedReturnTo()) {
        continueRequestedDownload();
        return true;
      }

      await loadSearchSchema();
      return true;
    }
  } catch (error) {
    console.warn("Server-Session konnte nicht wiederhergestellt werden:", error);
  }
  return false;
}

async function logout() {
  try {
    await api("/logout.php", { method: "POST", body: "{}" });
  } catch (error) {
    console.warn(error);
  }

  if (!privateOidc) {
    const account = msal.getActiveAccount() || msal.getAllAccounts()[0];
    if (account) {
      try {
        await msal.logoutPopup({ account, postLogoutRedirectUri: authRedirectUri });
      } catch (error) {
        console.warn(error);
      }
    }
  }

  el.search.value = "";
  showLoggedOut();
}

async function init() {
  if (privateOidc) {
    await configureOidcLogin();
  } else {
    await msal.initialize();
    const accounts = msal.getAllAccounts();
    if (accounts.length) msal.setActiveAccount(accounts[0]);
  }
  const restored = await restoreSession();
  if (privateOidc && !restored && new URLSearchParams(window.location.search).get('oidcError') === 'login_failed') {
    el.loginMessage.textContent = 'Die Nextcloud-Anmeldung wurde nicht abgeschlossen oder der Zugriff ist nicht freigegeben. Bitte erneut anmelden.';
    const url = new URL(window.location.href);
    url.searchParams.delete('oidcError');
    window.history.replaceState(null, '', url.pathname + url.search + url.hash);
  }
}

el.loginButton.addEventListener("click", login);
el.logoutButton.addEventListener("click", logout);
el.search.addEventListener("input", () => scheduleSearch());
el.searchField.addEventListener("change", () => scheduleSearch(0));

init().catch(error => {
  console.error(error);
  showLoggedOut(error.message || "Portal konnte nicht initialisiert werden.");
});
